/**
 * SES KANALI (voice bus) — TEK hoparlör kuralı.
 *
 * Neden: ses üç ayrı yerden çalıyordu (asistan cevabı, tur anlatımı, "düşünüyorum"
 * dolgusu) ve sayfa değişince ikinci bir sohbet bileşeni monte olup kendi sesini
 * başlatıyordu → sesler ÜST ÜSTE biniyordu.
 *
 * Bu modül uygulama genelinde tektir: yeni bir ses başlarken çalan ses HER ZAMAN
 * durdurulur. Öncelik vardır — "speech" (asistan/tur) çalarken gelen "filler"
 * (dolgu) sesi hiç başlatılmaz; tersi olursa dolgu susturulup söz öne alınır.
 *
 * ── MOBİL SES KİLİDİ ───────────────────────────────────────────────────────
 * Telefonda ses hiç gelmiyordu. Sebep: her cümle için `new Audio()` ile YENİ bir
 * eleman yaratılıyordu. iOS Safari ve Android Chrome yalnız KULLANICI HAREKETİ
 * sırasında oynatılmış bir elemanın sonradan programla çalmasına izin verir;
 * sonradan yaratılan elemanın play() çağrısı NotAllowedError ile reddedilir.
 * Eski kodda bu hata `.catch(finish)` ile sessizce yutuluyordu, bu yüzden
 * konsolda iz bırakmadan "sessizlik" olarak görünüyordu.
 *
 * Çözüm: TEK bir <audio> elemanı tutulur; ilk dokunuşta sessiz bir sesle
 * "açılır" (unlock) ve sonraki tüm seslendirmeler AYNI eleman üzerinden çalar.
 */
export type VoiceKind = "speech" | "filler";

// ── Tekil ses elemanı ───────────────────────────────────────────────────────
// HER seslendirmede yeniden yaratılmaz; mobil izni bu elemana bağlıdır.
let el: HTMLAudioElement | null = null;
let acildi = false;          // kullanıcı hareketiyle açıldı mı
let playId = 0;              // her oynatmaya benzersiz kimlik (paylaşılan eleman için gerekli)

function eleman(): HTMLAudioElement {
  if (el) return el;
  el = new Audio();
  el.preload = "auto";
  // iOS'ta tam ekran video oynatıcısına geçmesini engeller
  el.setAttribute("playsinline", "");
  return el;
}

/** 0,05 sn sessiz WAV — kilidi açmak için kullanılır (duyulmaz). */
const SESSIZ_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

/**
 * Ses kilidini açar. İlk kullanıcı hareketinde (dokunma/tık/tuş) çağrılır.
 * Sessiz bir ses çalıp hemen durdurur; eleman böylece "kullanıcı onaylı" sayılır.
 */
export function sesKilidiniAc(): void {
  if (acildi) return;
  const a = eleman();
  try {
    a.src = SESSIZ_WAV;
    a.muted = true;
    const p = a.play();
    if (p && typeof p.then === "function") {
      void p.then(() => {
        a.pause();
        a.muted = false;
        a.removeAttribute("src");
        acildi = true;
      }).catch(() => {
        // Bu hareket yeterli değilmiş; bir sonraki dokunuşta tekrar denenir.
        a.muted = false;
      });
    } else {
      a.pause(); a.muted = false; acildi = true;
    }
  } catch { a.muted = false; }
}

/** Kilit açma dinleyicileri — modül yüklenir yüklenmez kurulur. */
if (typeof document !== "undefined") {
  const ac = () => sesKilidiniAc();
  // once:true DEĞİL — ilk deneme başarısız olabilir (bazı tarayıcılar
  // pointerdown'u yeterli saymaz), açılana kadar her harekette denenir.
  for (const olay of ["pointerdown", "touchend", "click", "keydown"] as const) {
    document.addEventListener(olay, ac, { passive: true, capture: true });
  }
}

let current: { url: string; kind: VoiceKind; id: number } | null = null;

// ── SÖZ KUYRUĞU (streaming): cümleler üretildikçe sıraya girer, ardışık çalınır ──
const speechQueue: { blob: Blob; opts?: { onStart?: () => void } }[] = [];
let draining = false;

/** Yalnız ÇALAN sesi durdurur — kuyruğa dokunmaz (kuyruk akışının iç adımı). */
function stopCurrent(): void {
  if (!current) return;
  const { url } = current;
  current = null;
  const a = eleman();
  try { a.pause(); } catch { /* zaten durmuş */ }
  URL.revokeObjectURL(url);
}

/** Çalan sesi durdurur (varsa), KUYRUĞU boşaltır ve kaynağı serbest bırakır.
 *  KRİTİK HATA DERSİ (2026-08-21): playVoice her cümlenin başında bunu çağırıyordu —
 *  kuyruk temizliği kuyruğun KENDİ akışını da siliyordu ve asistan paragrafın yalnız
 *  İLK cümlesini okuyup susuyordu. Kuyruk içi geçişler artık stopCurrent kullanır;
 *  bu fonksiyon yalnız DIŞ kesintiler içindir (barge-in, sohbet kapatma, sessize alma). */
export function stopVoice(): void {
  speechQueue.length = 0;
  stopCurrent();
}

/** Streaming cümlesini kuyruğa ekler; kuyruk kendiliğinden sırayla çalar. */
export function enqueueSpeech(blob: Blob, opts?: { onStart?: () => void }): void {
  speechQueue.push({ blob, opts });
  if (!draining) void drainQueue();
}
async function drainQueue(): Promise<void> {
  draining = true;
  try {
    while (speechQueue.length) {
      const item = speechQueue.shift()!;
      await playVoice(item.blob, "speech", item.opts);
    }
  } finally { draining = false; }
}
/** Kuyruk + çalan ses tamamen bitene kadar bekler (sohbeti kapatırken veda yarım kalmasın). */
export async function waitSpeechEnd(): Promise<void> {
  while (draining || speechQueue.length || current?.kind === "speech") {
    await new Promise((r) => window.setTimeout(r, 150));
  }
}

export function isSpeaking(): boolean { return current?.kind === "speech"; }

/**
 * Sesi çalar ve BİTENE KADAR bekleyen promise döndürür.
 * onStart, ses gerçekten çalmaya başladığında çağrılır (orb durumu için).
 * Ses dışarıdan durdurulursa (stopVoice / yeni ses) promise yine çözülür — çağıran taraf asılı kalmaz.
 */
export function playVoice(blob: Blob, kind: VoiceKind, opts?: { onStart?: () => void; watchdogMs?: number }): Promise<void> {
  // Söz çalarken dolgu sesi ASLA araya girmez
  if (kind === "filler" && isSpeaking()) return Promise.resolve();
  stopCurrent(); // kuyruk KORUNUR — sıradaki cümleler silinmesin (ilk-cümle-sonrası sessizlik hatası)

  const url = URL.createObjectURL(blob);
  const id = ++playId;
  const a = eleman();
  current = { url, kind, id };

  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(watchdog);
      temizle();
      // Yalnız HÂLÂ bu oynatma güncelse durdur (yeni ses başladıysa ona dokunma).
      // Eleman paylaşıldığı için kimlik (id) ile ayırt ediyoruz. stopCurrent:
      // cümle bitişi kuyruğu SİLMEZ — sıradaki cümle çalmaya devam eder.
      if (current?.id === id) stopCurrent();
      resolve();
    };
    const temizle = () => {
      a.onended = null; a.onerror = null; a.onstalled = null; a.onpause = null;
    };
    const watchdog = window.setTimeout(finish, opts?.watchdogMs ?? 120_000);
    a.onended = finish;
    a.onerror = finish;
    a.onstalled = () => { if (a.paused) finish(); };
    // pause() dışarıdan gelirse de bitmiş say (barge-in / stopVoice)
    a.onpause = () => { if (!a.ended && a.currentTime > 0) finish(); };

    a.src = url;
    a.currentTime = 0;
    a.play()
      .then(() => opts?.onStart?.())
      .catch((err: unknown) => {
        // Mobilde kilit hâlâ açılmamışsa buraya düşer. Sessizce yutmak yerine
        // iz bırak: "ses gelmiyor" şikâyetinin sebebi konsolda görünsün.
        const ad = (err as { name?: string } | null)?.name ?? "";
        if (ad === "NotAllowedError") {
          console.warn("[ses] Tarayıcı oynatmayı engelledi — ekrana bir kez dokunulması gerekiyor.");
        }
        finish();
      });
  });
}
